// =============================================================================
// SimSense Platform — What's New Popup [OT-131]
// =============================================================================
// Dynamically fetches release notes from the backend.
// Shows a popup for the latest unread 'update' notification.

import { useState, useEffect } from "react";
import { api } from "../utils/api";

interface Notification {
  id: string;
  title: string;
  body: string;
  version?: string;
  type: "update" | "alert" | "maintenance";
  is_published: boolean;
  published_at?: string;
  is_seen: boolean;
}

export function WhatsNew() {
  const [activeNote, setActiveNote] = useState<Notification | null>(null);
  const [previousNotes, setPreviousNotes] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchNotifications() {
      try {
        const data = await api<Notification[]>("/api/notifications");
        if (data && data.length > 0) {
          // Find the latest unread 'update' notification
          const latestUpdate = data.find(n => n.type === "update" && !n.is_seen);
          if (latestUpdate) {
            setActiveNote(latestUpdate);
            setPreviousNotes(data.filter(n => n.id !== latestUpdate.id && n.type === "update"));
          }
        }
      } catch (err) {
        console.error("Failed to fetch notifications", err);
      } finally {
        setLoading(false);
      }
    }
    fetchNotifications();
  }, []);

  const dismiss = async () => {
    if (activeNote) {
      try {
        await api(`/api/notifications/${activeNote.id}/seen`, { method: "POST" });
      } catch (err) {
        console.error("Failed to mark as seen", err);
      }
      setActiveNote(null);
    }
  };

  if (loading || !activeNote) return null;

  // Split body into items (supports '+' or newlines)
  const items = activeNote.body
    .split(/\n|\+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999,
        padding: "20px",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: "12px",
          padding: "32px",
          maxWidth: "520px",
          width: "100%",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
          <div>
            <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
              What's New
            </div>
            <h2 style={{ fontSize: "22px", margin: 0 }}>{activeNote.title}</h2>
            <div style={{ fontSize: "12px", color: "var(--text2)", marginTop: "4px" }}>
              {activeNote.version ? `v${activeNote.version} — ` : ""}
              {activeNote.published_at ? new Date(activeNote.published_at).toLocaleDateString() : ""}
            </div>
          </div>
          <button
            onClick={dismiss}
            style={{ background: "none", border: "none", color: "var(--text2)", cursor: "pointer", fontSize: "20px", padding: "4px" }}
          >
            x
          </button>
        </div>

        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "10px", marginBottom: "24px" }}>
          {items.map((item, i) => (
            <li key={i} style={{ fontSize: "13px", lineHeight: 1.5, color: "var(--text)", display: "flex", gap: "8px" }}>
              <span style={{ color: "var(--accent)", flexShrink: 0 }}>+</span>
              {item}
            </li>
          ))}
        </ul>

        {previousNotes.length > 0 && (
          <details style={{ marginBottom: "20px" }}>
            <summary style={{ fontSize: "12px", color: "var(--text2)", cursor: "pointer", marginBottom: "12px" }}>
              Previous releases
            </summary>
            {previousNotes.slice(0, 3).map((release) => {
              const prevItems = release.body.split(/\n|\+/).map(s => s.trim()).filter(s => s.length > 0);
              return (
                <div key={release.id} style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
                    {release.version ? `v${release.version} — ` : ""}{release.title}
                    <span style={{ fontSize: "11px", color: "var(--text2)", fontWeight: 400, marginLeft: "8px" }}>
                      {release.published_at ? new Date(release.published_at).toLocaleDateString() : ""}
                    </span>
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "4px" }}>
                    {prevItems.slice(0, 2).map((item, i) => (
                      <li key={i} style={{ fontSize: "12px", color: "var(--text2)", display: "flex", gap: "6px" }}>
                        <span style={{ color: "var(--text3)" }}>-</span> {item}
                      </li>
                    ))}
                    {prevItems.length > 2 && <li style={{ fontSize: "11px", color: "var(--text3)", marginLeft: "14px" }}>...and {prevItems.length - 2} more</li>}
                  </ul>
                </div>
              );
            })}
          </details>
        )}

        <button
          onClick={dismiss}
          style={{
            width: "100%",
            padding: "12px",
            background: "var(--accent)",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontWeight: 600,
            fontSize: "14px",
            cursor: "pointer",
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
